import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrendingUp, Users, BarChart3, Database, Smartphone, FileText,
  Calendar, Video, StickyNote, Send, MessageCircle, Mail, Phone,
  CheckCircle, ArrowRight, Sparkles, Shield, Menu, X,
  AlertCircle, BookOpen, Newspaper, ChevronRight, ArrowUp,
  Facebook, Instagram, Youtube, Linkedin
} from 'lucide-react';
import { Radar, RadarChart as RechartsRadar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { contactAPI } from '../services/api';
import { getResponsiveCloudinaryUrls, getCloudinaryImageUrl, BACKGROUND_IMAGES, CARD_IMAGES } from '../utils/cloudinary';
import TherapistSignupModal from '../components/modals/TherapistSignupModal';
import Footer from '../components/layout/Footer';

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', whatsappNumber: '', message: '' });
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const isScrollingRef = useRef(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showTherapistSignupModal, setShowTherapistSignupModal] = useState(false);
  const navigate = useNavigate();

  // Responsive background images state
  const [backgroundImages, setBackgroundImages] = useState({
    hero: '',
    howItWorks: '',
    blogs: ''
  });

  // Responsive card images state
  const [cardImages, setCardImages] = useState({
    individual: '',
    couple: '',
    child: '',
    alternateTherapies: ''
  });

  // Lazy loading state - track which images should be loaded
  const [imagesToLoad, setImagesToLoad] = useState({
    hero: true,        // Load hero immediately (above the fold)
    howItWorks: false, // Lazy load
    blogs: false       // Lazy load
  });

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      isScrollingRef.current = true;
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
      // Reset the flag after scroll animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);
    }
  };

  const scrollToTop = () => {
    isScrollingRef.current = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Reset the flag and update FAB visibility after scroll animation completes
    setTimeout(() => {
      isScrollingRef.current = false;
      // Force update FAB visibility after scroll completes
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      setShowScrollToTop(scrollPosition > viewportHeight * 1.5);
    }, 1000);
  };

  // Update FAB visibility based on scroll position (always runs, even during programmatic scrolling)
  useEffect(() => {
    const handleScrollForFAB = () => {
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      // Show scroll-to-top button after scrolling ~1-2 viewport heights
      setShowScrollToTop(scrollPosition > viewportHeight * 1.5);
    };

    // Set initial state
    handleScrollForFAB();

    window.addEventListener('scroll', handleScrollForFAB, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollForFAB);
  }, []);

  // Detect active section based on scroll position
  useEffect(() => {
    const sections = ['how-it-works', 'blogs-news', 'lets-talk'];
    
    const handleScroll = () => {
      // Don't update active section if we're programmatically scrolling
      if (isScrollingRef.current) return;

      const scrollPosition = window.scrollY;
      const offset = 190; // Offset for navbar and sticky header (80px nav + padding)
      
      // Find the section that's currently in view
      let currentSection = '';
      
      // Check sections from bottom to top to find the one we've scrolled past
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          
          // If we've scrolled past the start of this section (accounting for offset)
          if (scrollPosition + offset >= sectionTop) {
            currentSection = sections[i];
            break;
          }
        }
      }
      
      // Update active section
      setActiveSection(prev => prev !== currentSection ? currentSection : prev);
    };

    // Set initial active section
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update background images based on screen size for responsive loading
  // Only loads images that are marked for loading (lazy loading)
  useEffect(() => {
    const updateBackgrounds = () => {
      const width = window.innerWidth;
      let size = 'desktop';
      let cardSize = 'desktop';

      if (width <= 768) {
        size = 'mobile';
        cardSize = 'mobile';
      } else if (width <= 1280) {
        size = 'tablet';
      }

      // Get responsive URLs for each background image
      const heroUrls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.backgroundImg2);
      const img4Urls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.backgroundImg4);
      const img6Urls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.backgroundImg6);

      setBackgroundImages(prev => ({
        hero: imagesToLoad.hero ? heroUrls[size] : prev.hero,
        howItWorks: imagesToLoad.howItWorks ? img6Urls[size] : prev.howItWorks,
        blogs: imagesToLoad.blogs ? img4Urls[size] : prev.blogs
      }));

      // Update card images based on screen size
      setCardImages({
        individual: getCloudinaryImageUrl(CARD_IMAGES.individual[cardSize]),
        couple: getCloudinaryImageUrl(CARD_IMAGES.couple[cardSize]),
        child: getCloudinaryImageUrl(CARD_IMAGES.child[cardSize]),
        alternateTherapies: getCloudinaryImageUrl(CARD_IMAGES.alternateTherapies[cardSize])
      });
    };

    // Set initial backgrounds (only for images that should be loaded)
    updateBackgrounds();

    // Update on window resize
    window.addEventListener('resize', updateBackgrounds);
    return () => window.removeEventListener('resize', updateBackgrounds);
  }, [imagesToLoad]);

  // Lazy load background images using Intersection Observer
  useEffect(() => {
    // Hero image loads immediately, so skip it
    const lazyLoadSections = [
      { sectionId: 'how-it-works', imageKey: 'howItWorks' },
      { sectionId: 'blogs-news', imageKey: 'blogs' }
    ];

    const observers = [];

    lazyLoadSections.forEach(({ sectionId, imageKey }) => {
      const section = document.getElementById(sectionId);
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // When section comes into view (with 200px margin for preloading)
            if (entry.isIntersecting) {
              setImagesToLoad(prev => ({
                ...prev,
                [imageKey]: true
              }));
              // Disconnect observer after loading to prevent unnecessary checks
              observer.disconnect();
            }
          });
        },
        {
          rootMargin: '200px', // Start loading 200px before section comes into view
          threshold: 0.01 // Trigger when even 1% of section is visible
        }
      );

      observer.observe(section);
      observers.push(observer);
    });

    // Cleanup: disconnect all observers on unmount
    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, []);

  // Sample data for chart visual
  const chartSampleData = [
    { field: 'Mental Clarity', value: 4 },
    { field: 'Emotions', value: 3.5 },
    { field: 'Relationships', value: 4.5 },
    { field: 'Physical Health', value: 3 },
    { field: 'Energy', value: 3.5 },
    { field: 'Sleep', value: 4 }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: null, message: '' });

    try {
      const response = await contactAPI.submit(formData);
      setFormStatus({
        type: 'success',
        message: response.data.message || 'Thank you for contacting us! We will get back to you soon.'
      });
      setFormData({ name: '', email: '', whatsappNumber: '', message: '' });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: error.response?.data?.error || 'Failed to submit form. Please try again later.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Client Management',
      description: 'Manage all your clients in one unified platform with easy assignment, tracking, and profile management.',
      color: 'text-blue-600'
    },
    {
      icon: Database,
      title: 'Data Analysis & Storage',
      description: 'Secure, comprehensive data storage and analytics with PostgreSQL database for reliable session history tracking.',
      color: 'text-purple-600'
    },
    {
      icon: Smartphone,
      title: 'Mobile Access',
      description: 'Access your practice from any device, anywhere. Fully responsive design ensures seamless experience on mobile, tablet, and desktop.',
      color: 'text-green-600'
    },
    {
      icon: FileText,
      title: 'Industry Reports',
      description: 'Generate professional reports with industry-standard metrics and custom report templates for comprehensive client documentation.',
      color: 'text-orange-600'
    },
    {
      icon: BarChart3,
      title: 'Visual Communication',
      description: 'Share progress charts and graphs with clients to visualize therapy progress and improvements over time.',
      color: 'text-primary-600',
      hasVisual: true
    },
    {
      icon: Calendar,
      title: 'Calendar Integration',
      description: 'Sync with Google Calendar for seamless appointment management and automated scheduling.',
      color: 'text-red-600'
    },
    {
      icon: Video,
      title: 'Video Sessions',
      description: 'Conduct online therapy sessions with integrated video capabilities for remote client consultations.',
      color: 'text-indigo-600'
    },
    {
      icon: StickyNote,
      title: 'Session Notes',
      description: 'Document sessions with comprehensive note-taking system and maintain complete session history.',
      color: 'text-yellow-600'
    },
    {
      icon: Send,
      title: 'Report Delivery',
      description: 'Automatically send reports to clients via email with professional formatting and secure delivery.',
      color: 'text-pink-600'
    }
  ];

  const benefits = [
    { text: 'Better client management with centralized database' },
    { text: 'Improved data organization and analytics' },
    { text: 'Enhanced client communication through visual charts' },
    { text: 'Streamlined workflow with calendar integration' },
    { text: 'Professional reporting with industry standards' }
  ];

  // Sample blog posts for Blogs & News section
  const blogPosts = [
    {
      id: 1,
      title: 'The Future of Therapy Practice Management',
      excerpt: 'Discover how modern technology is transforming therapy practices and improving patient care.',
      date: 'January 15, 2025',
      category: 'Technology'
    },
    {
      id: 2,
      title: 'Understanding Progress Visualization in Therapy',
      excerpt: 'Learn how visual charts and graphs can enhance client understanding and engagement.',
      date: 'January 10, 2025',
      category: 'Clinical'
    },
    {
      id: 3,
      title: 'Best Practices for Secure Client Data Management',
      excerpt: 'Essential guidelines for maintaining HIPAA compliance in digital therapy platforms.',
      date: 'January 5, 2025',
      category: 'Security'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation Bar */}
      <nav className="sticky top-0 left-0 right-0 z-40 bg-white/95 dark:bg-dark-bg-primary/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center p-2 shadow-md border border-yellow-500">
                <img
                  src="/TheraPTrackLogoBgRemoved.png"
                  alt="TheraP Track Logo"
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">
                TheraP Track
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/about"
                className="font-medium transition-colors text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500"
              >
                About
              </Link>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className={`font-medium transition-colors ${
                  activeSection === 'how-it-works'
                    ? 'text-primary-600 dark:text-dark-primary-500'
                    : 'text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500'
                }`}
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('blogs-news')}
                className={`font-medium transition-colors ${
                  activeSection === 'blogs-news'
                    ? 'text-primary-600 dark:text-dark-primary-500'
                    : 'text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500'
                }`}
              >
                Blogs & News
              </button>
              <Link
                to="/login"
                className="font-medium transition-colors text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500"
              >
                Login
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700 dark:text-dark-text-primary"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-3">
              <Link
                to="/about"
                className="block w-full text-left font-medium py-2 text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className={`block w-full text-left font-medium py-2 ${
                  activeSection === 'how-it-works'
                    ? 'text-primary-600 dark:text-dark-primary-500'
                    : 'text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500'
                }`}
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('blogs-news')}
                className={`block w-full text-left font-medium py-2 ${
                  activeSection === 'blogs-news'
                    ? 'text-primary-600 dark:text-dark-primary-500'
                    : 'text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500'
                }`}
              >
                Blogs & News
              </button>
              <Link
                to="/login"
                className="block w-full text-left font-medium py-2 text-gray-700 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-dark-primary-500"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Therapy Types Section */}
      <section className="pt-8 pb-16 bg-gradient-to-br from-primary-50 to-white dark:from-dark-bg-secondary dark:to-dark-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-4">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary text-center md:text-left">
                Support is always within reach
              </h2>
              <div className="flex items-center gap-3">
                <img
                  src="/Hipaa-Ready.png"
                  alt="HIPAA Ready"
                  className="h-10 w-auto"
                />
                <span className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary">
                  HIPAA Ready
                </span>
              </div>
            </div>
            <p className="text-lg text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto text-center">
              Choose the therapy type that best fits your needs
            </p>
          </div>

          {/* Therapy Type Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Individual Therapy Card */}
            <div
              onClick={() => navigate('/therapists', { state: { therapyType: 'Individual Therapy' } })}
              className="group cursor-pointer bg-white dark:bg-dark-bg-tertiary rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative hover:ring-1 hover:ring-yellow-500"
            >
              <div className="relative overflow-hidden">
                <img
                  src={cardImages.individual || "/individual_card.png"}
                  alt="Individual Therapy"
                  className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Circle with Arrow - appears on hover */}
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 group-hover:bg-yellow-500">
                  <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300" />
                </div>
              </div>
            </div>

            {/* Couples Therapy Card */}
            <div
              onClick={() => navigate('/therapists', { state: { therapyType: 'Couples Therapy' } })}
              className="group cursor-pointer bg-white dark:bg-dark-bg-tertiary rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative hover:ring-1 hover:ring-yellow-500"
            >
              <div className="relative overflow-hidden">
                <img
                  src={cardImages.couple || "/couple_card.png"}
                  alt="Couples Therapy"
                  className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Circle with Arrow - appears on hover */}
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 group-hover:bg-yellow-500">
                  <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300" />
                </div>
              </div>
            </div>

            {/* Child Therapy Card */}
            <div
              onClick={() => navigate('/therapists', { state: { therapyType: 'Child Therapy' } })}
              className="group cursor-pointer bg-white dark:bg-dark-bg-tertiary rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative hover:ring-1 hover:ring-yellow-500"
            >
              <div className="relative overflow-hidden">
                <img
                  src={cardImages.child || "/child_card.png"}
                  alt="Child Therapy"
                  className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Circle with Arrow - appears on hover */}
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 group-hover:bg-yellow-500">
                  <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300" />
                </div>
              </div>
            </div>

            {/* Alternative Therapies Card */}
            <div
              onClick={() => navigate('/therapists', { state: { therapyType: 'Alternative Therapies' } })}
              className="group cursor-pointer bg-white dark:bg-dark-bg-tertiary rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative hover:ring-1 hover:ring-yellow-500"
            >
              <div className="relative overflow-hidden">
                <img
                  src={cardImages.alternateTherapies || "/hypnotherapy_card.png"}
                  alt="Alternative Therapies"
                  className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Circle with Arrow - appears on hover */}
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 group-hover:bg-yellow-500">
                  <ArrowRight className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Current Homepage Content */}
      <section id="how-it-works" className="py-24 relative scroll-mt-28">
        {/* Subtle background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 dark:opacity-30"
          style={{
            backgroundImage: imagesToLoad.howItWorks && backgroundImages.howItWorks 
              ? `url('${backgroundImages.howItWorks}')` 
              : imagesToLoad.howItWorks 
                ? `url('/backgroundImg6.webp')` 
                : 'none'
          }}
        ></div>
        {/* Light overlay to ensure readability */}
        <div className="absolute inset-0 bg-white/10 dark:bg-dark-bg-primary/60"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              How It Works
            </h2>
            <div className="w-24 h-1 bg-primary-600 dark:bg-dark-primary-600 mx-auto mb-6"></div>
          </div>

          {/* Main Features */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Smart Scheduling and Availability */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Smart Scheduling and Availability
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Streamline your practice with professionalism and clarity. Create your availability session slots for the upcoming 4 weeks and publish for client view. This will allow clients to book sessions as per your schedule, which reduces manual back-and-forth communication.
              </p>
            </div>

            {/* Appointments, Sessions, and Two-way Reminders */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Appointments, Sessions, and Two-way Reminders
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Practice seamlessly with structured visualization of appointments and sessions inside the tool. Get session reminders synced through Google Calendar, and appointment reminders synced through WhatsApp notification - both for Therapists and clients. A two-way reminder system allows therapists to be more professional, organized, and timely, respecting professional boundaries and privacy.
              </p>
            </div>

            {/* Automatically Generated Links for Online Session */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Automatically Generated Links for Online Session
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                To save the time of Therapists in admin work and avoid manual conversations, a Google Meet link will be automatically created and sent to both Therapists and Clients for online sessions.
              </p>
            </div>

            {/* Record Session Notes */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Record Session Notes
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Record and organize session notes (ex., CH and MSE for Psychologists) in one single platform during the session, no need to change tabs, and write in paper and pencil. It supports professional documentation practices of all of your clients in one place, respecting professional judgement, confidentiality, and HIPAA-aligned security.
              </p>
            </div>

            {/* Secure Document Access */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Secure Document Access
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Attach past reports, prescriptions, and referrals of the clients through a Google Drive link in a single platform. It helps to organize and keep the records without duplicating or misplacing the important, sensitive documents.
              </p>
            </div>

            {/* Assessment and Automated Scoring */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Assessment and Automated Scoring
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Administer questionnaires with automatic scoring support (both direct and reverse scoring) to your clients - designed to assist therapists in recording the data and scoring while leaving interpretation and professional judgements entirely with the therapists.
              </p>
            </div>

            {/* Visual Progress Tracking */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Visual Progress Tracking
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Visualize your clients' progress with comparison charts, line graphs, or bar graphs that bring assessment data from respective sessions over time.
              </p>
            </div>

            {/* Report and Documentation */}
            <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
                Report and Documentation
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                Develop reports or prescriptions with customized templates and share with the clients over a single platform.
              </p>
            </div>
          </div>

          {/* Other Features */}
          <div className="bg-white dark:bg-dark-bg-tertiary p-8 rounded-lg shadow-md">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">
              Other Features
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  Payment Integration:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Collect session fees directly through the TheraP Track platform. Accept advance payments (any percentage of the session fee) to reduce no-shows after appointments and to maintain clarity about the fees.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  Dark Theme:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Dark theme is designed to reduce eye strain and help therapists continue extended clinical hours.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  All Device Friendly Platform:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Use TheraP Track from any devices (Mobile, Laptop, PC, Tablet, Mac, iPhone) from anywhere as per your convenient.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  Custom Branding:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  The Custom Branding feature is included to add more professionalism to your therapy practice.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  Events and Announcements:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Write your daily blogs, insights, and news for your patients.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  Priority Chat Support:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Get help from TheraP Track technical team within 24 hours
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-dark-text-primary mb-2">
                  Privacy is the Key:
                </h4>
                <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  TheraP Track aligns with HIPAA guidelines, provides a platform to record sensitive data respecting professional boundaries and ethics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blogs & News Section */}
      <section id="blogs-news" className="py-24 relative scroll-mt-28">
        {/* Background pattern */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-70 dark:opacity-70"
          style={{
            backgroundImage: imagesToLoad.blogs && backgroundImages.blogs 
              ? `url('${backgroundImages.blogs}')` 
              : imagesToLoad.blogs 
                ? `url('/backgroundImg4.webp')` 
                : 'none'
          }}
        ></div>
        {/* Overlay to ensure text readability */}
        <div className="absolute inset-0 bg-white/20 dark:bg-dark-bg-primary/60"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Blogs & News
            </h2>
            <div className="w-24 h-1 bg-primary-600 dark:bg-dark-primary-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto">
              Stay updated with the latest insights, tips, and news from the therapy world
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden group"
              >
                <div className="h-48 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-dark-bg-secondary dark:to-dark-bg-primary flex items-center justify-center">
                  <Newspaper className="h-16 w-16 text-primary-600 dark:text-dark-primary-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-primary-600 dark:text-dark-primary-500 uppercase tracking-wide">
                      {post.category}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                      {post.date}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary mb-3 group-hover:text-primary-600 dark:group-hover:text-dark-primary-500 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 dark:text-dark-text-secondary mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <button className="flex items-center text-primary-600 dark:text-dark-primary-500 font-medium hover:text-primary-700 dark:hover:text-dark-primary-400 transition-colors">
                    Read More
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-primary-600 dark:bg-dark-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition">
              View All Posts
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section (Let's Talk) */}
      <section id="lets-talk" className="py-24 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-dark-bg-secondary dark:to-dark-bg-primary scroll-mt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center gap-4 mb-6">
              <MessageCircle className="h-8 w-8 text-primary-600 dark:text-dark-primary-500" />
              <Mail className="h-8 w-8 text-primary-600 dark:text-dark-primary-500" />
              <Phone className="h-8 w-8 text-primary-600 dark:text-dark-primary-500" />
            </div>
            <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-dark-text-primary">Book a Free Demo or Talk to Us</h2>
            <p className="text-lg text-gray-600 dark:text-dark-text-secondary">
              We will get back to you within 24 hrs.
            </p>
          </div>
          
          <form onSubmit={handleContactSubmit} className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-xl p-8">
            {formStatus.type && (
              <div className={`mb-6 p-4 rounded-lg ${
                formStatus.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}>
                {formStatus.message}
              </div>
            )}
            
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Name <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary dark:placeholder-dark-text-tertiary"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Email <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary dark:placeholder-dark-text-tertiary"
                  placeholder="your.email@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  id="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary dark:placeholder-dark-text-tertiary"
                  placeholder="+1234567890"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  rows="5"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary dark:placeholder-dark-text-tertiary"
                  placeholder="Tell us how we can help you..."
                />
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary-600 dark:bg-dark-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Contact Me
                    <Send className="h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <Footer
        onContactClick={() => scrollToSection('contact')}
        onHowItWorksClick={() => scrollToSection('how-it-works')}
        onSignupClick={() => setShowTherapistSignupModal(true)}
      />

      {/* Scroll to Top FAB */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-4 z-[999] w-12 h-12 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1" />
        </button>
      )}

      {/* Therapist Signup Modal */}
      <TherapistSignupModal 
        isOpen={showTherapistSignupModal} 
        onClose={() => setShowTherapistSignupModal(false)} 
      />
    </div>
  );
};

export default Home;
